/**
 * URL Prepopulation Utility
 *
 * Parses URL query parameters and converts them to form answer_data format
 * for prepopulating form fields via URL.
 *
 * Example URL: form.workflow86.com/form/{form_id}?start_date=15-Mar-2023&team_name=Recruiting
 * This would prepopulate the fields with placeholders 'start_date' and 'team_name'
 *
 * Security considerations:
 * - Keys are validated to prevent prototype pollution
 * - Values are length-limited to prevent DoS
 * - Only alphanumeric keys with underscores are allowed (matching placeholder format)
 */

// System parameters that should not be treated as field prepopulation values
const SYSTEM_PARAMS = [
  'loginSuccess',
  'token',
  'code',
  'state',
  'error',
  'error_description',
];

// Dangerous keys that could lead to prototype pollution
const DANGEROUS_KEYS = [
  '__proto__',
  'constructor',
  'prototype',
  '__defineGetter__',
  '__defineSetter__',
  '__lookupGetter__',
  '__lookupSetter__',
];

// Maximum length for prepopulated values (prevents DoS via extremely long strings)
const MAX_VALUE_LENGTH = 10000;

// Regex for valid placeholder keys (alphanumeric and underscores only)
const VALID_KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Validates that a key is safe to use as an object property
 * @param {string} key - The key to validate
 * @returns {boolean} True if the key is safe
 */
const isValidKey = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }

  // Check against dangerous keys (case-insensitive)
  if (DANGEROUS_KEYS.includes(key.toLowerCase())) {
    return false;
  }

  // Check against system parameters
  if (SYSTEM_PARAMS.includes(key)) {
    return false;
  }

  // Validate key format matches placeholder naming convention
  // Placeholders can only contain letters, numbers, and underscores
  if (!VALID_KEY_REGEX.test(key)) {
    return false;
  }

  return true;
};

/**
 * Sanitizes a value for safe storage
 * @param {string} value - The value to sanitize
 * @returns {string} Sanitized value
 */
const sanitizeValue = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Trim and limit length
  let sanitized = value.trim().slice(0, MAX_VALUE_LENGTH);

  return sanitized;
};

/**
 * Parses URL query parameters and returns field prepopulation data
 * @returns {Object} Object with field keys and their prepopulated values
 */
export const parseUrlPrepopulationParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const prepopulationData = Object.create(null); // Use null prototype to prevent pollution

  urlParams.forEach((value, key) => {
    // Validate key is safe and matches placeholder format
    if (!isValidKey(key)) {
      return;
    }

    // Skip empty values
    if (!value || value.trim() === '') {
      return;
    }

    // Sanitize and store the value
    const sanitizedValue = sanitizeValue(value);
    if (sanitizedValue) {
      prepopulationData[key] = sanitizedValue;
    }
  });

  return prepopulationData;
};

/**
 * Converts URL prepopulation data to Redux answer_data format
 * @param {Object} prepopulationData - Object with field keys and values from URL
 * @returns {Object} Object formatted for Redux answer_data
 */
export const convertToAnswerDataFormat = (prepopulationData) => {
  const answerData = {};

  Object.entries(prepopulationData).forEach(([key, value]) => {
    answerData[key] = {
      key: key,
      answer: [value], // Form fields expect answer as an array
      question_text: '',
      format: 'text_answer',
      fromUrlPrepopulation: true, // Flag to identify URL-prepopulated values
    };
  });

  return answerData;
};

/**
 * Merges URL prepopulation data with existing session answer data
 * URL prepopulation should NOT override existing session data (user's saved work)
 * @param {Object} existingAnswerData - Existing answer data from session
 * @param {Object} urlAnswerData - Answer data from URL parameters
 * @returns {Object} Merged answer data
 */
export const mergeWithExistingAnswers = (existingAnswerData, urlAnswerData) => {
  const mergedData = { ...existingAnswerData };

  Object.entries(urlAnswerData).forEach(([key, urlValue]) => {
    // Only prepopulate if there's no existing answer for this field
    const existingAnswer = existingAnswerData?.[key];
    const hasExistingAnswer =
      existingAnswer?.answer &&
      existingAnswer.answer.length > 0 &&
      existingAnswer.answer[0] !== '' &&
      existingAnswer.answer[0] !== null &&
      existingAnswer.answer[0] !== undefined;

    if (!hasExistingAnswer) {
      mergedData[key] = urlValue;
    }
  });

  return mergedData;
};

/**
 * Main function to get prepopulated answer data from URL
 * @param {Object} existingAnswerData - Optional existing answer data from session
 * @returns {Object} Answer data with URL prepopulation merged in
 */
export const getUrlPrepopulatedAnswerData = (existingAnswerData = {}) => {
  const urlParams = parseUrlPrepopulationParams();

  // Return existing data if no URL params to prepopulate
  if (Object.keys(urlParams).length === 0) {
    return existingAnswerData;
  }

  const urlAnswerData = convertToAnswerDataFormat(urlParams);
  return mergeWithExistingAnswers(existingAnswerData, urlAnswerData);
};

/**
 * Checks if there are any URL prepopulation parameters
 * @returns {boolean} True if URL has prepopulation parameters
 */
export const hasUrlPrepopulationParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let hasParams = false;

  urlParams.forEach((value, key) => {
    if (!SYSTEM_PARAMS.includes(key) && value && value.trim() !== '') {
      hasParams = true;
    }
  });

  return hasParams;
};
